/**
 * Muse Noir embed loader — T-311 (2026-05-02).
 *
 * Standalone vanilla-JS bundle distributed at
 * `https://muse-noir.web.app/loader.js`. Retailers paste a single
 * <script> tag into their Shopify theme (Form 1 snippet, stamped
 * 2026-05-01):
 *
 *   <script
 *     src="https://muse-noir.web.app/loader.js"
 *     data-tenant="muse-optical"
 *     data-api="https://qbxzeqfp97.execute-api.us-east-1.amazonaws.com"
 *     async
 *   ></script>
 *
 * Boot sequence:
 *   1. Self-discover config from `data-*` attrs (config.ts)
 *   2. Run outer-ring health gate (healthProbe.ts)
 *   3. Mount Pulse launcher pill (launcher.ts)
 *   4. On click, inject iframe at muse-noir.web.app/index.html with
 *      tenant + api query params (iframe.ts). Iframe boots the noir
 *      widget; widget reads tenant config from the engine via T-418a.
 *   5. On muse:close from iframe, unmount overlay and re-show launcher.
 *
 * Bundle target: ≤5KB gzipped. No React, no framer-motion, no large
 * deps. CSS owns animations (vendor-free `@keyframes`).
 *
 * Companion: T-307's ShopifyRedirectAdapter handles checkout via
 * window.open from inside the widget's iframe — the loader does NOT
 * intercept muse:checkout. No postMessage choreography for cart.
 *
 * Retires prod's `experience-ecommerce-widget/src/components/0-Launch/LaunchWidget.tsx`
 * (970 LOC React launcher with holographic-green register) as part
 * of this same ticket.
 */

import { discoverConfig } from "./config";
import { runHealthGate } from "./healthProbe";
import { mountLauncher, hideLauncher, showLauncher } from "./launcher";
import { openIframe, closeIframe } from "./iframe";

(function init() {
  const config = discoverConfig();
  if (!config) return;
  if (!config.iframeSrc) {
    if (typeof console !== "undefined") {
      console.error(
        "[Muse] loader: could not derive iframe src — script tag must "
          + "have a fully-qualified `src` attribute",
      );
    }
    return;
  }

  // Mount the launcher in `available` state by default. The health
  // gate flips to `unavailable` after probe failure, and back if
  // slow-poll recovers later.
  let launcherHandle: ReturnType<typeof mountLauncher> | null = null;

  function onLauncherClick() {
    hideLauncher();
    openIframe({
      iframeSrc: config!.iframeSrc,
      cdnOrigin: config!.cdnOrigin,
      onClose: () => {
        showLauncher();
      },
    });
  }

  launcherHandle = mountLauncher(onLauncherClick);

  // Outer-ring gate runs in parallel with launcher mount. Default
  // state is `available` — if probe fails, we flip to `unavailable`
  // (greyed pill) within ~3s. If it succeeds, no visible change.
  // This avoids the user seeing a "loading" state on every page-load
  // for a backend that's almost always healthy.
  let cancelGate: (() => void) | null = null;

  if (config.apiBase) {
    cancelGate = runHealthGate(
      config.apiBase,
      (state) => launcherHandle?.setState(state),
      config.tenantId,
    );
  } else {
    // No data-api attr — legacy / sandbox mode. Skip the gate; assume
    // available.
    if (typeof console !== "undefined") {
      console.warn(
        "[Muse] loader: no data-api attribute — skipping health gate",
      );
    }
  }

  // Best-effort cleanup on page unload — not strictly required (browser
  // GCs everything on navigation) but tidies up dev hot-reload cycles.
  window.addEventListener("beforeunload", () => {
    if (cancelGate) cancelGate();
    if (launcherHandle) launcherHandle.destroy();
    closeIframe();
  });
})();
